import React, { useState } from 'react';
import { addProduct } from '../services/api';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';

const categories = ['Apparel', 'Appliances', 'Analog Watches', 'Backpacks', 'Footwear', 'Gift Items', 'Handbags', 'Helmets', 'Smart Watches', 'Speakers', 'Sports', 'Yoga'];

const AddProductPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        mrp: '',
        super_price: '',
        category: '',
        tags: '',
        productURL: '',
    });
    const [images, setImages] = useState(['']); // Array of image URLs
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (index, value) => {
        const newImages = [...images];
        newImages[index] = value;
        setImages(newImages);
    };

    const addImageField = () => {
        setImages([...images, '']);
    };

    const removeImageField = (index) => {
        if (images.length > 1) {
            const newImages = images.filter((_, i) => i !== index);
            setImages(newImages);
        }
    };

    const validatePrices = () => {
        const mrp = parseFloat(formData.mrp);
        const superPrice = parseFloat(formData.super_price);
        return !isNaN(mrp) && !isNaN(superPrice) && superPrice < mrp;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validatePrices()) {
            setError('Super Price must be less than MRP and both must be valid numbers');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('You must be logged in to add a product.');

            // Filter out empty image URLs (images are now optional)
            const validImages = images.filter(url => url.trim() !== '');

            const productData = {
                ...formData,
                mrp: parseFloat(formData.mrp),
                super_price: parseFloat(formData.super_price),
                tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
                images: validImages.length > 0 ? validImages : []
            };

            await addProduct(productData, token);
            setSuccess('Product added successfully!');
            setFormData({
                name: '', description: '', mrp: '', super_price: '',
                category: '', tags: '', productURL: ''
            });
            setImages(['']);
        } catch (err) {
            setError(err.message || 'Failed to add product');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate('/admin/dashboard');
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/login');
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                {/* Add navigation buttons */}
                <div className="flex justify-between mb-6">
                    <button
                        onClick={handleBack}
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                    >
                        Back to Dashboard
                    </button>
                    <button
                        onClick={handleLogout}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                        Logout
                    </button>
                </div>

                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="px-6 py-8 sm:p-10">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold text-gray-900">Add New Product</h2>
                            <p className="mt-2 text-sm text-gray-600">Fill in the details below to add a new product</p>
                        </div>

                        {error && <ErrorMessage message={error} />}
                        {success && (
                            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                                <p className="text-green-700 text-center">{success}</p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Left Column */}
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Product Name</label>
                                        <input
                                            id="name"
                                            name="name"
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            placeholder="Product Name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Description</label>
                                        <textarea
                                            id="description"
                                            name="description"
                                            required
                                            value={formData.description}
                                            onChange={handleChange}
                                            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            placeholder="Product Description"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Category</label>
                                        <select
                                            id="category"
                                            name="category"
                                            required
                                            value={formData.category}
                                            onChange={handleChange}
                                            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        >
                                            <option value="">Select Category</option>
                                            {categories.map((category, index) => (
                                                <option key={index} value={category}>{category}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Tags (comma-separated, optional)</label>
                                        <input
                                            id="tags"
                                            name="tags"
                                            type="text"
                                            value={formData.tags}
                                            onChange={handleChange}
                                            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            placeholder="e.g., new, sale, featured"
                                        />
                                        <p className="mt-1 text-sm text-gray-500">
                                            Optional: Add tags separated by commas
                                        </p>
                                    </div>
                                </div>

                                {/* Right Column */}
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">MRP (₹)</label>
                                        <input
                                            id="mrp"
                                            name="mrp"
                                            type="number"
                                            step="0.01"
                                            required
                                            value={formData.mrp}
                                            onChange={handleChange}
                                            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            placeholder="MRP"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Super Price (₹)</label>
                                        <input
                                            id="super_price"
                                            name="super_price"
                                            type="number"
                                            step="0.01"
                                            required
                                            value={formData.super_price}
                                            onChange={handleChange}
                                            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            placeholder="Super Price"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Image URLs
                                        </label>
                                        {images.map((imageUrl, index) => (
                                            <div key={index} className="mb-4">
                                                <div className="flex gap-2">
                                                    <input
                                                        type="url"
                                                        value={imageUrl}
                                                        onChange={(e) => handleImageChange(index, e.target.value)}
                                                        className="flex-1 appearance-none rounded-md relative block px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                        placeholder={`Image URL ${index + 1}`}
                                                    />
                                                    {images.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeImageField(index)}
                                                            className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                                {imageUrl && (
                                                    <div className="mt-2">
                                                        <img 
                                                            src={imageUrl} 
                                                            alt={`Preview ${index + 1}`} 
                                                            className="w-full h-48 object-cover rounded-md border border-gray-200"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={addImageField}
                                            className="mt-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
                                        >
                                            + Add Another Image
                                        </button>
                                        <p className="mt-2 text-sm text-gray-500">
                                            Add multiple image URLs for the product (optional)
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Product URL (Internal - Not Displayed)</label>
                                        <input
                                            id="productURL"
                                            name="productURL"
                                            type="url"
                                            value={formData.productURL}
                                            onChange={handleChange}
                                            className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            placeholder="Product Source URL (e.g., Flipkart, Amazon link)"
                                        />
                                        <p className="mt-1 text-sm text-gray-500">
                                            This URL is stored for internal reference and will not be displayed to users
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-smooth"
                                    disabled={loading}
                                >
                                    {loading ? <LoadingSpinner /> : 'Add Product'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddProductPage;